import { useMemo } from 'react'
import { useGetPathwaysQuery } from '../../features/SpeechAgendaApiSlice.ts'
import { Pathway } from '../../generated'
import { PathwayCode } from '../../types/Types.ts'

export interface PathwayGroup {
  group: string
  pathways: Pathway[]
}

// Wraps the GET /pathways metadata into the shapes the UI needs: a code -> label
// map for display, and pathways grouped (preserving backend order, e.g.
// Current / Vintage / Legacy) for the grouped <optgroup> dropdown. Nothing here
// is hardcoded — it all derives from the generated client / backend enum.
export function usePathways() {
  const query = useGetPathwaysQuery()
  const pathways = useMemo(() => query.data ?? [], [query.data])

  const labelByCode = useMemo(() => {
    const map = {} as Record<PathwayCode, string>
    pathways.forEach((pathway) => {
      map[pathway.code as PathwayCode] = pathway.label
    })
    return map
  }, [pathways])

  const groups = useMemo<PathwayGroup[]>(() => {
    const order: string[] = []
    const byGroup: Record<string, Pathway[]> = {}
    pathways.forEach((pathway) => {
      if (!byGroup[pathway.group]) {
        byGroup[pathway.group] = []
        order.push(pathway.group)
      }
      byGroup[pathway.group].push(pathway)
    })
    return order.map((group) => ({ group, pathways: byGroup[group] }))
  }, [pathways])

  // Resolve a pathway code to its backend-provided label, falling back to the
  // raw code only until the /pathways metadata has resolved.
  const labelForCode = (code: PathwayCode | undefined): string | undefined =>
    code ? (labelByCode[code] ?? code) : undefined

  return { ...query, pathways, labelByCode, labelForCode, groups }
}
